import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import type { GenesisEvent, PassEvent, TorchChain, ParsedChain, CanonicalPass } from './torchTypes';
import {
  TORCH_GENESIS_KIND,
  TORCH_PASS_KIND,
  DEFAULT_TTL_HOURS,
} from './torchConstants';

export function parseGenesisEvent(event: NostrEvent): GenesisEvent | null {
  if (event.kind !== TORCH_GENESIS_KIND) return null;

  const getTag = (name: string): string | undefined =>
    event.tags.find(t => t[0] === name)?.[1];

  const slug = getTag('d');
  if (!slug) return null;

  return {
    id: event.id,
    pubkey: event.pubkey,
    createdAt: event.created_at,
    slug,
    title: getTag('title') || 'Untitled Torch',
    description: getTag('description') || '',
    seed: getTag('seed') || event.id,
    ttlHours: parseInt(getTag('ttl') || String(DEFAULT_TTL_HOURS), 10),
  };
}

/**
 * Parse a kind 1910 Torch Pass event.
 *
 * The passer (current holder) publishes this after zapping the recipient.
 * Fields:
 *   pubkey        = passer
 *   p tag         = recipient (new holder)
 *   e tag (reply) = previous pass event id (or genesis id for the first pass)
 *   zap-receipt   = kind 9735 event id proving the zap landed
 *   zap-amount    = sats sent
 *   expires_at    = unix ts when this holder's TTL expires
 *   mark          = optional public message
 */
export function parsePassEvent(event: NostrEvent): PassEvent | null {
  if (event.kind !== TORCH_PASS_KIND) return null;

  const getTag = (name: string): string | undefined =>
    event.tags.find(t => t[0] === name)?.[1];

  const genesisRef = getTag('a');
  const recipientPubkey = getTag('p');
  const zapReceiptId = getTag('zap-receipt');

  if (!genesisRef || !recipientPubkey || !zapReceiptId) return null;

  // Diagnostic: warn about malformed receipts without rejecting them,
  // so existing chains remain intact while we detect any bad data in the wild.
  if (!/^[0-9a-f]{64}$/i.test(zapReceiptId)) {
    console.warn(`[torchEvents] Pass event ${event.id.slice(0, 16)} has non-hex zap-receipt: ${zapReceiptId.slice(0, 32)}…`);
  }

  const eTags = event.tags.filter(t => t[0] === 'e');
  const previousPassId = eTags.find(t => t[3] === 'reply')?.[1] || eTags[0]?.[1] || '';

  return {
    id: event.id,
    pubkey: event.pubkey,
    createdAt: event.created_at,
    genesisRef,
    previousPassId,
    recipientPubkey,
    zapReceiptId,
    zapAmountSats: parseInt(getTag('zap-amount') || '0', 10),
    mark: getTag('mark') || undefined,
    expiresAt: parseInt(getTag('expires_at') || '0', 10),
  };
}

/**
 * Build the canonical chain from a set of pass events.
 *
 * Two states only: lit or extinguished.
 * A torch is extinguished the moment the current holder's TTL expires.
 * No grace period, no rekindle — pass it or kill it.
 *
 * Rules:
 * 1. First valid pass must be signed by the genesis creator
 * 2. Each subsequent pass must be signed by the recipient of the previous pass
 * 3. Earlier created_at wins on ties (race condition resolution)
 * 4. zap-amount must exceed previous pass by at least 1 sat
 * 5. Pass must be published before the current holder's expires_at
 */
export function buildCanonicalChain(chain: TorchChain): ParsedChain {
  const now = Math.floor(Date.now() / 1000);

  // Sort passes by created_at ascending (earliest wins races)
  const sortedPasses = [...chain.passes].sort((a, b) => a.createdAt - b.createdAt);

  const canonicalChain: CanonicalPass[] = [];
  let currentHolder: string = chain.genesis.pubkey;
  let lastZapAmount = 0;

  // TTL clock starts at genesis creation
  let lastExpiresAt = chain.genesis.createdAt + (chain.genesis.ttlHours * 3600);

  for (const pass of sortedPasses) {
    // Must be signed by the current holder
    if (pass.pubkey !== currentHolder) continue;

    // Zap amount must exceed the previous pass
    if (pass.zapAmountSats < lastZapAmount + 1) continue;

    // Must be published before the current TTL expired — miss the window and it's done
    if (pass.createdAt > lastExpiresAt) continue;

    canonicalChain.push({
      pass,
      position: canonicalChain.length + 1,
    });

    currentHolder = pass.recipientPubkey;
    lastZapAmount = pass.zapAmountSats;
    lastExpiresAt = pass.expiresAt > 0
      ? pass.expiresAt
      : pass.createdAt + (chain.genesis.ttlHours * 3600);
  }

  // Two states: lit (within TTL) or extinguished (TTL expired)
  const state = now < lastExpiresAt ? 'lit' : 'extinguished';
  const hoursUntilExtinguished = Math.max(0, lastExpiresAt - now) / 3600;

  return {
    genesis: chain.genesis,
    canonicalChain,
    state,
    currentHolder,
    expiresAt: lastExpiresAt,
    chainLength: canonicalChain.length,
    lastZapAmount,
    hoursUntilExtinguished: state === 'lit' ? hoursUntilExtinguished : null,
  };
}

export function genesisRef(genesis: GenesisEvent): string {
  return `${TORCH_GENESIS_KIND}:${genesis.pubkey}:${genesis.slug}`;
}

export function genesisNaddr(genesis: GenesisEvent, relays: string[] = []): string {
  try {
    return nip19.naddrEncode({
      kind: TORCH_GENESIS_KIND,
      pubkey: genesis.pubkey,
      identifier: genesis.slug,
      relays,
    });
  } catch {
    return '';
  }
}
